export type Vec3 = [number, number, number];
export type Mat4 = Float32Array;

export function vec3(x = 0, y = 0, z = 0): Vec3 {
  return [x, y, z];
}

export function lerp(a: number, b: number, amount: number): number {
  return a + (b - a) * amount;
}

export function damp(current: number, target: number, speed: number, delta: number): number {
  return lerp(current, target, 1 - Math.exp(-speed * delta));
}

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function mat4Identity(): Mat4 {
  return new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
}

export function mat4Perspective(fieldOfView: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fieldOfView / 2);
  const range = 1 / (near - far);
  return new Float32Array([
    f / aspect,
    0,
    0,
    0,
    0,
    f,
    0,
    0,
    0,
    0,
    (far + near) * range,
    -1,
    0,
    0,
    2 * far * near * range,
    0,
  ]);
}

export function mat4Orthographic(
  left: number,
  right: number,
  bottom: number,
  top: number,
  near: number,
  far: number,
): Mat4 {
  const width = right - left;
  const height = top - bottom;
  const depth = far - near;
  return new Float32Array([
    2 / width,
    0,
    0,
    0,
    0,
    2 / height,
    0,
    0,
    0,
    0,
    -2 / depth,
    0,
    -(right + left) / width,
    -(top + bottom) / height,
    -(far + near) / depth,
    1,
  ]);
}

export function mat4LookAt(eye: Vec3, target: Vec3, up: Vec3 = [0, 1, 0]): Mat4 {
  let zx = eye[0] - target[0];
  let zy = eye[1] - target[1];
  let zz = eye[2] - target[2];
  let length = Math.hypot(zx, zy, zz) || 1;
  zx /= length;
  zy /= length;
  zz /= length;
  let xx = up[1] * zz - up[2] * zy;
  let xy = up[2] * zx - up[0] * zz;
  let xz = up[0] * zy - up[1] * zx;
  length = Math.hypot(xx, xy, xz) || 1;
  xx /= length;
  xy /= length;
  xz /= length;
  const yx = zy * xz - zz * xy;
  const yy = zz * xx - zx * xz;
  const yz = zx * xy - zy * xx;
  return new Float32Array([
    xx,
    yx,
    zx,
    0,
    xy,
    yy,
    zy,
    0,
    xz,
    yz,
    zz,
    0,
    -(xx * eye[0] + xy * eye[1] + xz * eye[2]),
    -(yx * eye[0] + yy * eye[1] + yz * eye[2]),
    -(zx * eye[0] + zy * eye[1] + zz * eye[2]),
    1,
  ]);
}

export function mat4Multiply(a: Mat4, b: Mat4): Mat4 {
  const result = new Float32Array(16);
  for (let column = 0; column < 4; column += 1) {
    for (let row = 0; row < 4; row += 1) {
      let value = 0;
      for (let index = 0; index < 4; index += 1) {
        value += a[index * 4 + row]! * b[column * 4 + index]!;
      }
      result[column * 4 + row] = value;
    }
  }
  return result;
}

export function mat4Transform(
  x: number,
  y: number,
  z: number,
  yaw = 0,
  scaleX = 1,
  scaleY = scaleX,
  scaleZ = scaleX,
): Mat4 {
  const cosine = Math.cos(yaw);
  const sine = Math.sin(yaw);
  return new Float32Array([
    cosine * scaleX,
    0,
    -sine * scaleX,
    0,
    0,
    scaleY,
    0,
    0,
    sine * scaleZ,
    0,
    cosine * scaleZ,
    0,
    x,
    y,
    z,
    1,
  ]);
}

export function mat4TransformEuler(
  x: number,
  y: number,
  z: number,
  yaw: number,
  pitch: number,
  roll: number,
  scaleX = 1,
  scaleY = scaleX,
  scaleZ = scaleX,
): Mat4 {
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cx = Math.cos(pitch);
  const sx = Math.sin(pitch);
  const cz = Math.cos(roll);
  const sz = Math.sin(roll);
  return new Float32Array([
    (cy * cz + sy * sx * sz) * scaleX,
    cx * sz * scaleX,
    (-sy * cz + cy * sx * sz) * scaleX,
    0,
    (-cy * sz + sy * sx * cz) * scaleY,
    cx * cz * scaleY,
    (sy * sz + cy * sx * cz) * scaleY,
    0,
    sy * cx * scaleZ,
    -sx * scaleZ,
    cy * cx * scaleZ,
    0,
    x,
    y,
    z,
    1,
  ]);
}

/** Uniform-scale ground transform whose up axis is partially aligned to terrain. */
export function mat4TerrainTransform(
  x: number,
  y: number,
  z: number,
  yaw: number,
  scale: number,
  terrainNormal: readonly [number, number, number],
  normalAlignment: number,
): Mat4 {
  const result = new Float32Array(16);
  writeTerrainTransform(result, 0, x, y, z, yaw, scale, terrainNormal, normalAlignment);
  return result;
}

export function writeUprightTransform(
  target: Float32Array,
  offset: number,
  x: number,
  y: number,
  z: number,
  yaw: number,
  scale: number,
): void {
  const cosine = Math.cos(yaw);
  const sine = Math.sin(yaw);
  target[offset] = cosine * scale;
  target[offset + 1] = 0;
  target[offset + 2] = -sine * scale;
  target[offset + 3] = 0;
  target[offset + 4] = 0;
  target[offset + 5] = scale;
  target[offset + 6] = 0;
  target[offset + 7] = 0;
  target[offset + 8] = sine * scale;
  target[offset + 9] = 0;
  target[offset + 10] = cosine * scale;
  target[offset + 11] = 0;
  target[offset + 12] = x;
  target[offset + 13] = y;
  target[offset + 14] = z;
  target[offset + 15] = 1;
}

export function writeTerrainTransform(
  target: Float32Array,
  offset: number,
  x: number,
  y: number,
  z: number,
  yaw: number,
  scale: number,
  terrainNormal: readonly [number, number, number],
  normalAlignment: number,
): void {
  let upX = terrainNormal[0] * normalAlignment;
  let upY = 1 + (terrainNormal[1] - 1) * normalAlignment;
  let upZ = terrainNormal[2] * normalAlignment;
  let length = Math.hypot(upX, upY, upZ) || 1;
  upX /= length;
  upY /= length;
  upZ /= length;

  const headingX = Math.sin(yaw);
  const headingZ = Math.cos(yaw);
  let rightX = upY * headingZ;
  let rightY = upZ * headingX - upX * headingZ;
  let rightZ = -upY * headingX;
  length = Math.hypot(rightX, rightY, rightZ) || 1;
  rightX /= length;
  rightY /= length;
  rightZ /= length;
  const forwardX = rightY * upZ - rightZ * upY;
  const forwardY = rightZ * upX - rightX * upZ;
  const forwardZ = rightX * upY - rightY * upX;

  target[offset] = rightX * scale;
  target[offset + 1] = rightY * scale;
  target[offset + 2] = rightZ * scale;
  target[offset + 3] = 0;
  target[offset + 4] = upX * scale;
  target[offset + 5] = upY * scale;
  target[offset + 6] = upZ * scale;
  target[offset + 7] = 0;
  target[offset + 8] = forwardX * scale;
  target[offset + 9] = forwardY * scale;
  target[offset + 10] = forwardZ * scale;
  target[offset + 11] = 0;
  target[offset + 12] = x;
  target[offset + 13] = y;
  target[offset + 14] = z;
  target[offset + 15] = 1;
}

export function transformPoint(matrix: Mat4, point: Vec3): [number, number, number, number] {
  const [x, y, z] = point;
  return [
    matrix[0]! * x + matrix[4]! * y + matrix[8]! * z + matrix[12]!,
    matrix[1]! * x + matrix[5]! * y + matrix[9]! * z + matrix[13]!,
    matrix[2]! * x + matrix[6]! * y + matrix[10]! * z + matrix[14]!,
    matrix[3]! * x + matrix[7]! * y + matrix[11]! * z + matrix[15]!,
  ];
}
